import { IkeDelayedTask } from '@ikegps/core-shared';
import { action, computed, observable, makeObservable } from 'mobx';
import { m, type MChild } from '../../mithril';
import type { MithrilVNode } from '../../mithril';
import { Services } from '../../services';
import { InputEventBindContext } from '../../services/input/event.context';
import type { EventData } from '../../services/input/input.service';
import { IMithrilStateComponent } from '../mithril.component';
import { addStyles } from '../theme';
import { type MenuItem, MenuItemWrapperComponent } from './menu.item';
import { CSS, Style } from './menu.style';

addStyles(Style);

function resolveAnchorPosition(source?: MouseEvent | HTMLElement): { top: number; right: number } | null {
	if (source == null) {
		return null;
	}
	const el =
		source instanceof HTMLElement
			? source
			: ((source.currentTarget as HTMLElement | null) ?? (source.target as HTMLElement | null));
	if (el == null) {
		return null;
	}
	const rect = el.getBoundingClientRect();
	return { top: Math.round(rect.bottom + 4), right: Math.round(window.innerWidth - rect.right) };
}

export interface MenuComponentAttrs {
	open?: () => boolean;
	className?: string;
	items: () => MenuItem[];
}

export class MenuComponent extends IMithrilStateComponent {
	className: string;
	itemsFunc: () => MenuItem[];

	closeTask = new IkeDelayedTask(() => this.remove(), 200);

	@observable isOpen = false;
	@observable isClosing = false;
	@observable isOpening = false;
	@observable selectedItemIndex: number = 0;
	@observable anchorPosition: { top: number; right: number } | null = null;

	openFunc: () => boolean;

	eventContext: InputEventBindContext;

	constructor(attrs: MenuComponentAttrs) {
		super();

		makeObservable(this);

		this.className = attrs.className;
		this.itemsFunc = attrs.items;
		this.openFunc = attrs.open;
		this.eventContext = Services.Input.getContext();
	}

	override onCreate() {
		if (typeof this.openFunc === 'function') {
			this.reaction(() => this.openFunc(), this.toggle, { fireImmediately: true });
		}
	}

	get nodes(): MithrilVNode {
		if (!this.isOpen) {
			return null;
		}

		let className = `${CSS.menu} ${this.isOpen ? CSS.isOpen : CSS.isClosed}`;
		if (this.className != null) {
			className += ` ${this.className}`;
		}
		if (this.isOpening) {
			className += ` ${CSS.isOpening}`;
		}
		if (this.isClosing) {
			className += ` ${CSS.isClosing}`;
		}

		const style =
			this.anchorPosition != null
				? { position: 'fixed', top: `${this.anchorPosition.top}px`, right: `${this.anchorPosition.right}px` }
				: undefined;

		const items = this.items;
		if (items == null || items.length === 0) {
			return null;
		}

		const children: MChild[] = [];
		for (let i = 0; i < items.length; i++) {
			const menuItem = items[i];

			if (menuItem.component) {
				children.push(menuItem.view);
			} else {
				const selected = i === this.selectedItemIndex;
				children.push(MenuItemWrapperComponent.view(this, menuItem, selected));
			}

			if (menuItem.divider) {
				children.push(m('div', { className: CSS.divider }));
			}
		}

		return m(
			'div',
			{
				className,
				style,
				'data-testid': 'menu',
				oncreate: (node: MithrilVNode) => {
					Services.FocusAway.from(node.dom, this.hide);
					this.startOpenTransition();
				},
			},
			children
		);
	}

	@action.bound private startOpenTransition() {
		if (!this.isOpening) {
			return;
		}
		requestAnimationFrame(() =>
			requestAnimationFrame(() => {
				this.isOpening = false;
			})
		);
	}

	@computed
	private get items(): MenuItem[] {
		const menuItems = this.itemsFunc();
		if (menuItems == null) {
			return [];
		}

		return menuItems;
	}

	@computed get hasUsableItems(): boolean {
		const items = this.items;
		for (const item of items) {
			if (item.visible !== false && !item.disabled) {
				return true;
			}
		}

		return false;
	}

	@action.bound remove() {
		this.isOpen = false;
		this.isClosing = false;
		this.isOpening = false;
		this.anchorPosition = null;
	}

	@action.bound hide() {
		this.toggle(false);
	}

	@action.bound show(eventOrAnchor?: MouseEvent | HTMLElement) {
		this.anchorPosition = resolveAnchorPosition(eventOrAnchor);
		this.toggle(true);
	}

	@action.bound toggle(shouldOpen: boolean) {
		if (shouldOpen) {
			const wasOpen = this.isOpen;
			this.isOpen = true;
			this.isClosing = false;
			this.isOpening = !wasOpen;
			this.closeTask.cancel();
			this.bindKeyboardEvents();

			const selectedIndex = this.items.findIndex((i) => i.selected);
			this.selectedItemIndex = Math.max(0, selectedIndex);
		} else {
			this.isClosing = true;
			this.isOpening = false;
			if (this.closeTask.isActive) {
				return;
			}
			this.closeTask.delay();
			this.eventContext.destroy();
		}
	}

	@action selectItem(item: MenuItem) {
		const selectedIndex = this.items.findIndex((i) => i === item);
		this.selectedItemIndex = Math.max(0, selectedIndex);
	}

	@action.bound
	private selectCurrentItem(e: EventData) {
		e.event.stopPropagation();
		e.event.preventDefault();

		const menuItem = this.getSelectedItem();
		if (menuItem != null && menuItem.action != null) {
			menuItem.action();
		}

		this.hide();
	}

	private getSelectedItem(): MenuItem | null {
		return this.items[this.selectedItemIndex];
	}

	@action
	private selectItemIndex(delta: number) {
		const items = this.items;

		let index = this.wrap(this.selectedItemIndex + delta, items.length);
		while (items[index].visible === false || items[index].disabled) {
			if (index === this.selectedItemIndex) {
				// No other selectable items. Stop
				return;
			}

			index = this.wrap(index + delta, items.length);
		}

		this.selectedItemIndex = index;
	}

	private wrap(index: number, count: number): number {
		return ((index % count) + count) % count;
	}

	private bindKeyboardEvents() {
		this.eventContext.on(this.eventContext.Code.ArrowUp, () => this.selectItemIndex(-1));
		this.eventContext.on(this.eventContext.Code.ArrowDown, () => this.selectItemIndex(1));
		this.eventContext.on(this.eventContext.Code.Enter, this.selectCurrentItem);
		this.eventContext.on(this.eventContext.Code.Escape, this.hide);
	}

	override onRemove() {
		super.onRemove();
		this.eventContext.destroy();
	}
}
